import { TfNotice, useNoticeMutation } from 'api/notices';
import { parseJSON } from 'date-fns';
import { useTranslations } from 'i18n';
import mixpanel from 'mixpanel-browser';
import * as React from 'react';
import { Button, ModalComponent } from 'ui';
import ReactMarkdown from 'react-markdown';
import { UpdateDetails } from 'updates/update-details';
import styles from 'updates/updates.module.css';
import { NotificationView } from './notification-view';

interface UpdateListItemProps {
    notice: TfNotice;
}

interface UpdateModalProps {
    notice: TfNotice;
    open: boolean;
    onClose: () => void;
}

export function UpdateListItem({ notice }: UpdateListItemProps) {
    const { __ } = useTranslations();
    const { mutate: updateNotice } = useNoticeMutation();
    const [isOpen, setIsOpen] = React.useState(false);

    function setRead(read: boolean) {
        mixpanel.track(read ? 'NotificationRead' : 'NotificationUnread', {
            notification_title: notice.title,
            notification_id: notice.id,
        });
        updateNotice({ id: notice.id, read });
    }

    function showDetails(open: boolean) {
        setIsOpen(open);
        mixpanel.track('NotificationModal', { modal: open ? 'open' : 'close', notification_id: notice.id });
        if (open && !notice.read) {
            setRead(true);
        }
    }

    return (
        <>
            <NotificationView
                title={notice.title}
                contents={
                    <span className={styles['summary']}>
                        <ReactMarkdown allowedElements={[]} unwrapDisallowed>
                            {notice.copy}
                        </ReactMarkdown>
                    </span>
                }
                createdAt={parseJSON(notice.published_at)}
                isRead={!!notice.read}
                onToggleRead={() => setRead(!notice.read)}
                actions={[
                    <Button
                        key="details"
                        size="sm"
                        color="primary"
                        label={__('misc.more_details')}
                        onClick={() => showDetails(true)}
                        className="notification-action-button"
                    />,
                ]}
            />
            <UpdateModal notice={notice} open={isOpen} onClose={() => showDetails(false)} />
        </>
    );
}

export function UpdateModal({ notice, open, onClose }: UpdateModalProps) {
    return (
        <ModalComponent
            openModal={open}
            size="3xl"
            title={notice.title}
            subTitle={notice.published_at_formatted ?? undefined}
            showFooter={false}
            handleClose={onClose}
            handleCancel={onClose}
            handleConfirm={onClose}
        >
            <UpdateDetails notice={notice} />
        </ModalComponent>
    );
}
